import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Icon } from '@iconify/react';
import { Link } from 'react-router-dom';
import { useConfig } from '../utils/ConfigContext.jsx';
import { createAxiosInstance } from '../utils/AxiosInstance.jsx';

const UsersListLayer = () => {
  const { API_BASE_URL } = useConfig();
  const AxiosInstance = createAxiosInstance(API_BASE_URL);
  const [users, setUsers] = useState([]);
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(false);

  const [query, setQuery] = useState({
    page: 1,
    per_page: 10,
    search: '',
    status: '',
  });

  // 🔹 Fetch users
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await AxiosInstance.get(
        '/users',
        { params: query }
      );

      setUsers(res.data.data || []);
      setMeta(res.data || {});
    } catch (error) {
      console.error('Failed to fetch users', error);
    } finally {
      setLoading(false);
    }
  };

  const changeStatus = async (userId, newStatus) => {
    if (loading) return; // Prevent multiple requests
    const confirmation = window.confirm("Are you sure you want to change the status?");
    if (!confirmation) return;
    try {
      await AxiosInstance.patch(`/users/${userId}`, {
        status: newStatus,
      });
      fetchUsers(); // Refresh the list
    } catch (error) {
      console.error('Failed to change status', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (userId) => {
    if (loading) return;
    const confirmation = window.confirm("Are you sure you want to delete this user?");
    if (!confirmation) return;
    try {
      await AxiosInstance.delete(`/users/${userId}`);
      fetchUsers(); // Refresh the list
    } catch (error) {
      console.error('Failed to delete user', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [query]);

  return (
    <div className="card h-100 p-0 radius-12 pb-4 mb-5">
      {/* ================= HEADER ================= */}
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex flex-wrap gap-3 justify-content-between">
        <div className="d-flex flex-wrap gap-3 align-items-center">
          {/* Per page */}
          <span className="text-md fw-medium text-secondary-light">Show</span>
          <select
            className="form-select form-select-sm w-auto"
            value={query.per_page}
            onChange={(e) =>
              setQuery({ ...query, per_page: e.target.value, page: 1 })
            }
          >
            {[10, 25, 50, 100].map(n => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>

          {/* Search */}
          <div className="navbar-search">
            <input
              type="text"
              className="bg-base h-40-px w-auto"
              placeholder="Search"
              onChange={(e) =>
                setQuery({ ...query, search: e.target.value, page: 1 })
              }
            />
            <Icon icon="ion:search-outline" className="icon" />
          </div>

          {/* Status */}
          <select
            className="form-select form-select-sm w-auto"
            onChange={(e) =>
              setQuery({ ...query, status: e.target.value, page: 1 })
            }
          >
            <option value="">All Status</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
        </div>

        <Link
          to="/users/create"
          className="btn btn-primary btn-sm d-flex align-items-center gap-2"
        >
          <Icon icon="ic:baseline-plus" className="text-xl" />
          Add New User
        </Link>
      </div>

      {/* ================= BODY ================= */}
      <div className="card-body p-24">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr>
                <th>S.L</th>
                <th>Name</th>
                <th>Email/Phone</th>
                <th>Designation</th>
                <th className="text-center">Status</th>
                <th className="text-center">Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" className="text-center">Loading...</td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center">No users found</td>
                </tr>
              ) : (
                users.map((user, index) => (
                  <tr key={user.id}>
                    <td>{(meta.from ?? 1) + index}</td>
                    <td>
                      <div className="d-flex align-items-center">
                        <img
                          src={user.profile_picture ?? ''}
                          alt=""
                          className="w-40-px h-40-px rounded-circle me-12"
                        />
                        <span className="text-secondary-light">
                          {user.salutation} {user.first_name} {user.last_name}
                        </span>
                      </div>
                    </td>

                    <td>{user.email} <br />{user.phone}</td>

                    <td>{user.designation}</td>

                    <td className="text-center">
                      {user.status === 1 ? (
                        <span className="bg-success-focus text-success-600 px-24 py-4 radius-4 cursor-pointer" onClick={() => changeStatus(user.id, 0)}>
                          Active
                        </span>
                      ) : (
                        <span className="bg-neutral-200 text-neutral-600 px-24 py-4 radius-4 cursor-pointer " onClick={() => changeStatus(user.id, 1)} >
                          Inactive
                        </span>
                      )}
                    </td>

                    <td className="text-center">
                      <div className="d-flex gap-10 justify-content-center">
                        {/* <button className="bg-info-focus w-40-px h-40-px rounded-circle d-flex justify-content-center align-items-center">
                          <Icon icon="majesticons:eye-line" />
                        </button> */}
                        <Link to={`/users/${user.id}/edit`}>
                          <button className="bg-success-focus w-40-px h-40-px rounded-circle d-flex justify-content-center align-items-center">
                            <Icon icon="lucide:edit" />
                          </button>
                        </Link>

                        <button className="bg-danger-focus w-40-px h-40-px rounded-circle d-flex justify-content-center align-items-center" onClick={() => deleteUser(user.id)} >
                          <Icon icon="fluent:delete-24-regular" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* ================= FOOTER ================= */}
        <div className="d-flex justify-content-between flex-wrap gap-2 mt-24">
          <span>
            Showing {meta.from ?? 0} to {meta.to ?? 0} of {meta.total ?? 0} entries
          </span>

          <ul className="pagination d-flex gap-2">
            <li className={`page-item ${!meta.prev_page_url && 'disabled'}`}>
              <button
                className="page-link"
                onClick={() =>
                  setQuery({ ...query, page: meta.current_page - 1 })
                }
                disabled={!meta.prev_page_url}
              >
                <Icon icon="ep:d-arrow-left" />
              </button>
            </li>

            {Array.from({ length: meta.last_page || 1 }).map((_, i) => (
              <li key={i} className="page-item">
                <button
                  className={`page-link ${meta.current_page === i + 1
                    ? 'bg-primary-600 text-white'
                    : 'bg-neutral-200'
                    }`}
                  onClick={() =>
                    setQuery({ ...query, page: i + 1 })
                  }
                >
                  {i + 1}
                </button>
              </li>
            ))}

            <li className={`page-item ${!meta.next_page_url && 'disabled'}`}>
              <button
                className="page-link"
                onClick={() =>
                  setQuery({ ...query, page: meta.current_page + 1 })
                }
                disabled={!meta.next_page_url}
              >
                <Icon icon="ep:d-arrow-right" />
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default UsersListLayer;
